import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import {
  Plane,
  Sparkles,
  Shield,
  Droplets,
  Clock,
  CheckCircle,
  Menu,
  X,
  ChevronRight,
  Star,
  Phone,
  Mail,
  MapPin,
  ArrowUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Floating CTA Button
const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
      <a
        href="tel:7276324244"
        data-testid="floating-phone-btn"
        className="w-14 h-14 bg-green-600 hover:bg-green-500 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
      >
        <Phone className="w-6 h-6 text-white" />
      </a>
      <a
        href="#contact"
        data-testid="floating-cta-btn"
        className="w-14 h-14 bg-[#007AFF] hover:bg-[#0066DD] rounded-full flex items-center justify-center shadow-lg btn-glow transition-all hover:scale-110"
      >
        <Mail className="w-6 h-6 text-white" />
      </a>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "Pricing", href: "#pricing" },
    { name: "Add-Ons", href: "#addons" },
    { name: "Gallery", href: "#gallery" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "nav-blur py-4" : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a
          href="#hero"
          data-testid="logo-link"
          className="flex items-center gap-3"
        >
          <div className="bg-white rounded-lg p-1.5">
            <img 
              src="https://customer-assets.emergentagent.com/job_sky-shine-1/artifacts/kidon95l_SOLID%20BLUE%20LOGO.png" 
              alt="AVICLEAN Logo" 
              className="h-8 w-auto"
            />
          </div>
          <span className="text-2xl font-extrabold text-white font-['Syne'] tracking-tight">
            AVICLEAN
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              data-testid={`nav-${link.name.toLowerCase()}`}
              className="text-gray-300 hover:text-white transition-colors text-sm font-medium tracking-wide"
            >
              {link.name}
            </a>
          ))}
          <a href="#contact">
            <Button
              data-testid="nav-cta-btn"
              className="bg-[#007AFF] hover:bg-[#0066DD] text-white rounded-full px-6 btn-glow"
            >
              Get Quote
            </Button>
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden absolute top-full left-0 right-0 nav-blur py-6"
        >
          <div className="flex flex-col gap-4 px-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-300 hover:text-white transition-colors py-2"
              >
                {link.name}
              </a>
            ))}
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="bg-[#007AFF] hover:bg-[#0066DD] text-white rounded-full w-full mt-2">
                Get Quote
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1540962351504-03099e0a754b?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <p className="text-[#007AFF] text-sm md:text-base font-semibold tracking-[0.2em] uppercase mb-6 animate-fade-in-up opacity-0">
          Premium Aircraft Detailing
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up opacity-0 delay-100 font-['Syne']">
          Precision Detailing for
          <span className="gradient-text block mt-2">Every Aircraft</span>
        </h1>
        <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-6 animate-fade-in-up opacity-0 delay-200">
          Aviation-approved products. Checklist-driven process. Zero shortcuts.
          We detail aircraft the way pilots expect them to be maintained.
        </p>
        
        {/* Trust Badge */}
        <div className="flex items-center justify-center gap-2 mb-10 animate-fade-in-up opacity-0 delay-200">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <Clock className="w-4 h-4 text-[#007AFF]" />
            <span className="text-sm text-gray-300">Free quotes within 24 hours</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up opacity-0 delay-300">
          <a href="#contact">
            <Button
              data-testid="hero-cta-primary"
              className="bg-[#007AFF] hover:bg-[#0066DD] text-white rounded-full px-8 py-6 text-lg btn-glow"
            >
              Get Your Free Quote
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <a href="tel:7276324244">
            <Button
              data-testid="hero-cta-phone"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg bg-transparent"
            >
              <Phone className="mr-2 w-5 h-5" />
              (727) 632-4244
            </Button>
          </a>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-8 md:gap-16 mt-16 animate-fade-in-up opacity-0 delay-400">
          {[
            { value: "100%", label: "Satisfaction Rate" },
            { value: "FAA", label: "Compliant Products" },
            { value: "Insured", label: "Full Coverage" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white font-['Syne']">
                {stat.value}
              </p>
              <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Exterior Detailing",
      description:
        "Complete exterior wash, decontamination, polish, and ceramic coating to restore your aircraft's pristine finish.",
      features: ["Hand Wash & Dry", "Paint Correction", "Ceramic Protection"],
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Interior Detailing",
      description:
        "Deep cleaning of leather, carpets, headliners, and all interior surfaces with aviation-grade products.",
      features: ["Leather Conditioning", "Carpet Extraction", "Odor Removal"],
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Disinsection Services",
      description:
        "FAA-compliant aircraft disinsection to meet international travel requirements and ensure passenger safety.",
      features: ["Residual Treatment", "Pre-Flight Spraying", "Compliance Documentation"],
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Brightwork Polishing",
      description:
        "Expert polishing of all metal surfaces including chrome, stainless steel, and aluminum to a mirror finish.",
      features: ["Chrome Restoration", "Aluminum Polishing", "Protective Sealant"],
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Fleet Services",
      description:
        "Scheduled maintenance programs for charter companies and FBOs with volume pricing available.",
      features: ["Scheduled Service", "Volume Discounts", "Priority Booking"],
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#050505] relative noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Our Services
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Syne']">
            Comprehensive Aircraft Care
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Every service tailored to your aircraft's specific needs, from
            single-engine props to large-cabin jets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              data-testid={`service-card-${index}`}
              className="service-card relative p-8 rounded-xl group"
            >
              <div className="text-[#007AFF] mb-4 group-hover:scale-110 transition-transform">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-['Syne']">
                {service.title}
              </h3>
              <p className="text-gray-400 mb-6">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-center gap-2 text-gray-300 text-sm"
                  >
                    <CheckCircle className="w-4 h-4 text-[#007AFF]" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Section CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">Not sure which service you need?</p>
          <a href="#contact">
            <Button
              data-testid="services-cta"
              className="bg-[#007AFF] hover:bg-[#0066DD] text-white rounded-full px-8 py-5 btn-glow"
            >
              Get Expert Advice
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const plans = [
    {
      name: "Small Aircraft",
      subtitle: "Cessna, Piper, Beechcraft",
      price: "From $350",
      features: [
        "Full Exterior Wash & Dry",
        "Interior Vacuum & Wipe Down",
        "Window Cleaning",
        "Basic Leather Treatment",
        "Tire & Wheel Detail",
      ],
      featured: false,
    },
    {
      name: "Mid-Size Jets",
      subtitle: "Citation, Phenom, Learjet",
      price: "From $850",
      features: [
        "Everything in Small Aircraft",
        "Paint Decontamination",
        "Deep Carpet Extraction",
        "Leather Conditioning",
        "Engine Bay Cleaning",
        "Ceramic Sealant Application",
      ],
      featured: true,
    },
    {
      name: "Large Cabin",
      subtitle: "Gulfstream, Global, Falcon",
      price: "From $1,800",
      features: [
        "Everything in Mid-Size",
        "Multi-Step Paint Correction",
        "Full Interior Restoration",
        "Premium Ceramic Coating",
        "Galley & Lavatory Detail",
        "Priority Scheduling",
      ],
      featured: false,
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-[#0A0A0A] relative"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Pricing
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Syne']">
            Transparent Pricing
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Custom quotes available for specific requirements. Contact us for
            fleet and recurring service discounts.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              data-testid={`pricing-card-${index}`}
              className={`pricing-card relative p-8 rounded-2xl ${
                plan.featured ? "featured" : ""
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#007AFF] text-white text-xs font-semibold px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white font-['Syne']">
                  {plan.name}
                </h3>
                <p className="text-gray-500 text-sm mt-1">{plan.subtitle}</p>
              </div>
              <p className="text-3xl font-bold text-white mb-8 font-['Syne']">
                {plan.price}
              </p>
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-3 text-gray-300 text-sm"
                  >
                    <CheckCircle className="w-4 h-4 text-[#007AFF] mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <a href="#contact">
                <Button
                  data-testid={`pricing-cta-${index}`}
                  className={`w-full rounded-full py-5 ${
                    plan.featured
                      ? "bg-[#007AFF] hover:bg-[#0066DD] text-white btn-glow"
                      : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  }`}
                >
                  Get Started
                </Button>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Add-On Services Section
const AddOnServicesSection = () => {
  const addOns = [
    { service: "Heavy Belly Degrease", price: "$75–$150" },
    { service: "Plexiglass Restoration", price: "$150–$300" },
    { service: "Leather Deep Conditioning", price: "$100–$200" },
    { service: "Oxidation Spot Correction", price: "$200–$400" },
    { service: "Engine Bay Detail", price: "$150–$300", note: "Approved access only" },
    { service: "Ceramic Coating Top-Up", price: "$250–$400" },
    { service: "Pet Hair Removal", price: "From $80" },
  ];

  return (
    <section
      id="addons"
      data-testid="addons-section"
      className="py-24 md:py-32 bg-[#050505] relative noise-overlay"
    >
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Customize Your Detail
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Syne']">
            Add-On Services
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Enhance your detailing package with these specialized services.
          </p>
        </div>

        <div className="glass rounded-2xl overflow-hidden">
          <div className="divide-y divide-white/10">
            {addOns.map((item, index) => (
              <div
                key={item.service}
                data-testid={`addon-item-${index}`}
                className="flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-5 h-5 text-[#007AFF]" />
                  <div>
                    <span className="text-white font-medium">{item.service}</span>
                    {item.note && (
                      <span className="text-gray-500 text-sm ml-2">({item.note})</span>
                    )}
                  </div>
                </div>
                <span className="text-[#007AFF] font-semibold font-['Syne']">
                  {item.price}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <a href="#contact">
            <Button
              data-testid="addons-cta"
              className="bg-[#007AFF] hover:bg-[#0066DD] text-white rounded-full px-8 py-6 btn-glow"
            >
              Request Custom Quote
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const galleryImages = [
    {
      url: "https://images.unsplash.com/photo-1768346564233-d71f37bd19b6?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Luxury jet interior",
    },
    {
      url: "https://images.unsplash.com/photo-1764705639498-7d982f668c38?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Cessna Caravan turboprop on tarmac",
    },
    {
      url: "https://images.unsplash.com/photo-1476115962940-76cbae013188?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Shiny polished jet exterior",
    },
    {
      url: "https://images.unsplash.com/photo-1593938346024-7ee982d8224b?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Cessna small aircraft",
    },
    {
      url: "https://images.unsplash.com/photo-1767868281232-c2805a3c5b9e?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Turboprop aircraft engine detail",
    },
    {
      url: "https://images.unsplash.com/photo-1566827267844-39de9bcad5ee?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Polished private jet on tarmac",
    },
  ];

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Our Work
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Syne']">
            Gallery
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Showcasing our attention to detail on aircraft of all sizes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryImages.map((img, index) => (
            <div
              key={index}
              data-testid={`gallery-item-${index}`}
              className={`gallery-item ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
            >
              <img
                src={img.url}
                alt={img.alt}
                className={`w-full object-cover rounded-xl ${
                  index === 0 ? "h-full min-h-[400px]" : "h-64"
                }`}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const standards = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Aviation-Approved Products Only",
      description: "We use only products designed and approved for aircraft use. Automotive chemicals can damage plexiglass, paint, seals, and composites. We never use ammonia-based cleaners, silicone dressings, or harsh degreasers.",
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Checklist-Driven Process",
      description: "Every service follows a standardized checklist modeled after aviation maintenance best practices. This ensures consistency, no missed steps, and repeatable quality on every job.",
    },
    {
      icon: <Plane className="w-6 h-6" />,
      title: "Component Awareness",
      description: "We take extra care around static ports, pitot systems, control surfaces, antennas, and sensors. No pressure washing near sensitive areas. No forced access or disassembly without approval.",
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Interior & Cockpit Safety",
      description: "We use non-reflective, non-silicone, non-greasy products to preserve instrument panels, avionics bezels, and controls without altering tactile feel or visibility.",
    },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#050505] relative noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Hero Statement */}
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
            Trust, Safety & Standards
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 font-['Syne']">
            Your Aircraft Deserves Better
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            Your aircraft is a regulated, safety-critical asset. We approach detailing with the same mindset as aviation maintenance: approved products, controlled processes, and zero shortcuts.
          </p>
        </div>

        {/* Standards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {standards.map((item, index) => (
            <div
              key={item.title}
              data-testid={`standard-card-${index}`}
              className="service-card relative p-8 rounded-xl group"
            >
              <div className="text-[#007AFF] mb-4 group-hover:scale-110 transition-transform">
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-['Syne']">
                {item.title}
              </h3>
              <p className="text-gray-400">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Commitment & Insurance Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-2xl font-bold text-white mb-6 font-['Syne']">
              Our Commitment
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              We detail aircraft the way pilots expect them to be maintained—carefully, methodically, and with respect for the machine. Our goal is simple: protect your aircraft, preserve its value, and support safe, confident flight.
            </p>
            
            <h4 className="text-lg font-semibold text-white mb-4 font-['Syne']">
              Respect for Boundaries
            </h4>
            <p className="text-gray-400 mb-6 leading-relaxed">
              We are detail professionals, not mechanics. We do not remove panels without approval, disconnect components, or perform maintenance. Any work requiring mechanical access is coordinated with the owner or A&P.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: <Shield className="w-5 h-5" />, text: "General Liability Insurance" },
                { icon: <Shield className="w-5 h-5" />, text: "Hangar Keepers Insurance" },
                { icon: <CheckCircle className="w-5 h-5" />, text: "Airport Compliant" },
                { icon: <CheckCircle className="w-5 h-5" />, text: "FBO Coordinated" },
              ].map((item) => (
                <div
                  key={item.text}
                  className="flex items-center gap-3 text-gray-300"
                >
                  <span className="text-[#007AFF]">{item.icon}</span>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="glass p-8 rounded-2xl">
              <h4 className="text-lg font-semibold text-white mb-4 font-['Syne']">
                Before Every Service
              </h4>
              <ul className="space-y-3 text-gray-400">
                {[
                  "Aircraft type and configuration confirmed",
                  "Hangared vs tied-down storage reviewed",
                  "Last wash or detail documented",
                  "Owner or maintenance restrictions noted",
                  "Corrosion or wear issues communicated",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-[#007AFF] mt-1 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-sm text-gray-500">
                  Certificates of insurance available upon request.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    aircraftType: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        aircraftType: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="text-[#007AFF] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              Get Started Today
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 font-['Syne']">
              Request Your Free Quote
            </h2>
            <p className="text-gray-400 mb-4">
              Tell us about your aircraft and service needs. We'll provide a
              custom quote within 24 hours.
            </p>
            
            {/* Urgency Banner */}
            <div className="bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-lg p-4 mb-8">
              <p className="text-[#007AFF] font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Limited Availability
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Book now to secure your preferred date. We service aircraft on a first-come, first-served basis.
              </p>
            </div>

            <div className="space-y-4">
              <a href="tel:7276324244" className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group">
                <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Call Now</p>
                  <p className="text-white font-semibold text-lg">(727) 632-4244</p>
                </div>
              </a>
              <a href="mailto:info@aviclean.org" className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group">
                <div className="w-12 h-12 rounded-full bg-[#007AFF]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Email</p>
                  <p className="text-white">info@aviclean.org</p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
                <div className="w-12 h-12 rounded-full bg-[#007AFF]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Service Area</p>
                  <p className="text-white">All airports from Vero Beach to Miami</p>
                </div>
              </div>
            </div>
            
            {/* Trust Signals */}
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Shield className="w-4 h-4 text-[#007AFF]" />
                Fully Insured
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <CheckCircle className="w-4 h-4 text-[#007AFF]" />
                FAA Compliant
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Star className="w-4 h-4 text-[#007AFF]" />
                100% Satisfaction
              </div>
            </div>
          </div>

          <div className="glass p-8 rounded-2xl">
            {/* Form Header */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white font-['Syne']">Get Your Free Quote</h3>
              <p className="text-gray-400 text-sm mt-1">No obligation • Response within 24 hours</p>
            </div>
            
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-5"
            >
              <div>
                <Input
                  name="name"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="form-input w-full rounded-lg px-4 py-3 bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#007AFF]"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="form-input w-full rounded-lg px-4 py-3 bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#007AFF]"
                />
                <Input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="contact-phone-input"
                  className="form-input w-full rounded-lg px-4 py-3 bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#007AFF]"
                />
              </div>
              <div>
                <Select
                  value={formData.aircraftType}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, aircraftType: value }))
                  }
                >
                  <SelectTrigger
                    data-testid="contact-aircraft-select"
                    className="form-input w-full rounded-lg px-4 py-3 bg-[#1A1A1A] border-white/10 text-white h-auto"
                  >
                    <SelectValue placeholder="Select Aircraft Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="small" className="text-white hover:bg-white/10">
                      Small Aircraft (Cessna, Piper)
                    </SelectItem>
                    <SelectItem value="turboprop" className="text-white hover:bg-white/10">
                      Turboprop (King Air, Pilatus)
                    </SelectItem>
                    <SelectItem value="light-jet" className="text-white hover:bg-white/10">
                      Light Jet (Citation, Phenom)
                    </SelectItem>
                    <SelectItem value="midsize-jet" className="text-white hover:bg-white/10">
                      Mid-Size Jet (Learjet, Hawker)
                    </SelectItem>
                    <SelectItem value="large-cabin" className="text-white hover:bg-white/10">
                      Large Cabin (Gulfstream, Global)
                    </SelectItem>
                    <SelectItem value="helicopter" className="text-white hover:bg-white/10">
                      Helicopter
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Tell us about your detailing needs..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid="contact-message-input"
                  className="form-input w-full rounded-lg px-4 py-3 bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#007AFF] resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="w-full bg-[#007AFF] hover:bg-[#0066DD] text-white rounded-full py-6 text-lg btn-glow disabled:opacity-50"
              >
                {isSubmitting ? "Sending..." : "Get My Free Quote"}
              </Button>
              
              {/* Form Trust Signal */}
              <p className="text-center text-gray-500 text-xs mt-4">
                🔒 Your information is secure and will never be shared
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="py-12 bg-[#050505] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <a href="#hero" className="flex items-center gap-2">
            <div className="bg-white rounded-lg p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_sky-shine-1/artifacts/kidon95l_SOLID%20BLUE%20LOGO.png" 
                alt="AVICLEAN Logo" 
                className="h-6 w-auto"
              />
            </div>
            <span className="text-xl font-extrabold text-white font-['Syne'] tracking-tight">
              AVICLEAN
            </span>
          </a>

          <div className="flex flex-wrap gap-6">
            {["Services", "Pricing", "Add-Ons", "Gallery", "About", "Contact"].map(
              (link) => (
                <a
                  key={link}
                  href={`#${link.toLowerCase().replace('-', '')}`}
                  className="footer-link text-sm"
                >
                  {link}
                </a>
              )
            )}
          </div>

          <button
            onClick={scrollToTop}
            data-testid="scroll-to-top-btn"
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#007AFF] transition-colors flex items-center justify-center"
          >
            <ArrowUp className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="section-divider my-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-500 text-sm">
          <p>© 2024 AVICLEAN. All rights reserved.</p>
          <p>Premium Aircraft Detailing Services</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="bg-[#050505] min-h-screen">
      <Toaster position="top-right" theme="dark" />
      <Navigation />
      <FloatingCTA />
      <HeroSection />
      <ServicesSection />
      <PricingSection />
      <AddOnServicesSection />
      <GallerySection />
      <AboutSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
